import { AUTH_CONFIG, STORAGE_KEYS } from '../constants/app.constants'

/**
 * Pair-model access-token refresh for the BackOffice staff login. This app's
 * `/login` already goes through the shared QL-User auth backend (same host as
 * every other QL frontend — see AUTH_CONFIG.BASE_URL), which issues a
 * `refresh_token` alongside the JWT `access_token`. Unlike the cookie-based
 * consumer apps, this SPA has no server-side layer and stores everything in
 * localStorage, so the refresh call reads/writes STORAGE_KEYS directly instead
 * of cookies.
 *
 * Single-flight: concurrent callers (keepalive timer, 401 retry) share ONE
 * in-flight refresh. Plain `fetch` (not the shared axios clients) so no
 * response interceptor can recurse into this helper.
 */

let inFlight: Promise<string | null> | null = null

const buildRefreshUrl = (): string => {
  const base = (AUTH_CONFIG.BASE_URL ?? '').replace(/\/+$/, '')
  return `${base}${AUTH_CONFIG.REFRESH_ENDPOINT}`
}

const clearStoredAuth = (): void => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.USER_DATA)
}

const doRefresh = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  if (!refreshToken) return null
  try {
    const res = await fetch(buildRefreshUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ refresh_token: refreshToken }).toString(),
    })
    if (!res.ok) {
      // 401 → the refresh token is invalid/expired: the session is dead and
      // cannot be revived. Clear the stale tokens so the app treats the user
      // as logged out. Transient failures (5xx/network) are NOT logout.
      if (res.status === 401) {
        clearStoredAuth()
      }
      return null
    }
    const data = (await res.json().catch(() => null)) as {
      access_token?: string
      refresh_token?: string
    } | null
    const newToken = data?.access_token
    if (!newToken) return null

    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken)
    if (data?.refresh_token) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token)
    }
    return newToken
  } catch {
    return null
  }
}

/** Single-flight refresh. Returns the new access token, or null on failure. */
export const refreshAccessToken = (): Promise<string | null> => {
  if (inFlight) return inFlight
  const p = (async (): Promise<string | null> => {
    try {
      return await doRefresh()
    } finally {
      inFlight = null
    }
  })()
  inFlight = p
  return p
}

/** True if a refresh token is present (i.e. a refresh is possible). */
export const hasRefreshToken = (): boolean =>
  !!localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
